import { useEffect } from 'react';

import { usePathname, useRouter, useUnstableGlobalHref } from 'expo-router';

export const REPLACE_ROOT_NAVIGATION_PARAM = 'navigation';
export const REPLACE_ROOT_NAVIGATION = 'replace-root';

export const getReplaceRootDestination = ({
  href,
  pathname,
}: {
  href: string | undefined;
  pathname: string;
}): string | null => {
  if (!href) {
    return null;
  }

  const queryIndex = href.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }

  // Use the index of the first `?` rather than `href.split('?')` so a destination
  // that itself contains a `?` (for example a nested redirect URL) is preserved
  // instead of being silently truncated.
  const query = href.slice(queryIndex + 1);

  const params = new URLSearchParams(query);
  if (params.get(REPLACE_ROOT_NAVIGATION_PARAM) !== REPLACE_ROOT_NAVIGATION) {
    return null;
  }

  params.delete(REPLACE_ROOT_NAVIGATION_PARAM);
  const targetQuery = params.toString();

  return targetQuery ? `${pathname}?${targetQuery}` : pathname;
};

/**
 * A deep link tagged with `navigation=replace-root` intentionally abandons the
 * current navigation context (for example, an Order Confirmation modal) before
 * opening its destination. Braze CTAs use this contract when their destination
 * must not retain the screen from which the in-app message was opened.
 */
export const ReplaceRootNavigationHandler = () => {
  const href = useUnstableGlobalHref();
  const pathname = usePathname();
  const router = useRouter();
  const destination = getReplaceRootDestination({ href, pathname });

  useEffect(() => {
    if (!destination) {
      return;
    }

    // Only dismiss when there is actually a stack to dismiss, matching the
    // guarded dismissAll()+navigate pattern used in use-cart-hydration.ts.
    if (router.canDismiss()) {
      router.dismissAll();
    }
    router.replace(destination);
  }, [destination, router]);

  return null;
};
